import { useEffect, useState } from 'react'
import { Formik, Form, FormikProps, FormikHelpers } from 'formik'
import { useQueryClient } from '@tanstack/react-query'
import { useHistory } from 'react-router-dom'
import {
  DomainSelector,
  Modal,
  TextField,
  useMessage,
  useGlobalState,
  useContentRefreshDomains,
} from '@pepper/ui'
import { attachDomainWithProject } from '@pepper/utils/src/api/seo'
import { createProject } from 'utils/api/project'
import { getDefaultItemType } from 'utils/functions/getDefaultItemType'
import * as Yup from 'yup'

import s from './CreateProject.module.css'

type CreateProjectProps = {
  isOpen: boolean
  companyId: string | undefined
  onClose: () => void
}

type FormValues = {
  project_title: string
}

const validationSchema = Yup.object({
  project_title: Yup.string().required().label('Project name'),
})

export const CreateProject = ({
  isOpen,
  companyId,
  onClose,
}: CreateProjectProps) => {
  const history = useHistory()
  const [selectedDomain, setSelectedDomain] = useState<string>()
  const { showSuccess, showError } = useMessage()
  const queryClient = useQueryClient()
  const { permissions } = useGlobalState()

  const { data: domains } = useContentRefreshDomains(companyId)

  const handleSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    const response = await createProject(values.project_title, companyId!)
    if (response.status === 'ok') {
      if (selectedDomain?.length) {
        const attachDomainResponse = await attachDomainWithProject({
          companyId: companyId!,
          projectId: response.data.project_id,
          domainId: selectedDomain!,
        })
        if (attachDomainResponse.status === 'ok') {
          const tab = permissions?.cmp ? 'topics' : 'items'
          const search =
            tab === 'items'
              ? getDefaultItemType(!!permissions?.marketplace)
              : undefined

          history.push({
            pathname: `/project/${response.data.project_id}/${tab}`,
            search,
          })
          showSuccess('Project created successfully')
          actions.resetForm()
        } else {
          showError('Unable to create project. Please try again')
        }
      } else {
        showSuccess('Project created successfully')
      }

      await queryClient.invalidateQueries({
        queryKey: ['/projects'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['project-domain-map'],
      })
      onClose()
    } else {
      showError(response?.error)
    }
  }

  useEffect(() => {
    if (domains.length > 0) {
      setSelectedDomain(domains?.[0]?.id)
    }
  }, [domains?.length])

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={{ project_title: '' }}
      validationSchema={validationSchema}
    >
      {(props: FormikProps<FormValues>): React.ReactNode => (
        <Modal
          visible={isOpen}
          className={s.modal}
          onCancel={() => {
            props.resetForm()
            onClose()
          }}
          okText="Create Project"
          title="Create New Project"
          onOk={props.submitForm}
          width={540}
          okButtonProps={{
            htmlType: 'submit',
            loading: props.isSubmitting,
            disabled: !props.isValid,
          }}
          closable={true}
          destroyOnClose={true}
        >
          <Form>
            <div className={s.fieldGroup}>
              <label className={s.label} htmlFor="project_title">
                Project Name
              </label>
              <TextField name="project_title" />
            </div>
            <div className={s.fieldGroup}>
              <label className={s.label} htmlFor="project_domain">
                Project Domain
              </label>
              <div className={s.domainSelect}>
                <DomainSelector
                  companyId={companyId!}
                  isControlled={true}
                  size="regular"
                  onChange={setSelectedDomain}
                  value={selectedDomain}
                />
              </div>
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  )
}
